import { useCallback, useState } from 'react';

import type { Posicion } from '@haus/core';

/**
 * La posición para el sello de la prueba.
 *
 * **La precisión importa tanto como la coordenada.** Un GPS que dice "estoy
 * aquí ±2000 m" no demuestra que el vídeo se grabó en ese apartamento, y por
 * eso se guarda `acc` junto a las coordenadas y el panel la enseña. Un sello
 * que parezca preciso sin serlo es peor que no tener sello.
 *
 * Nunca bloquea: si el permiso se deniega o tarda, la limpieza sigue. La
 * prueba es el vídeo con su hash; el GPS la refuerza.
 */
export function useGps(): {
    posicion: Posicion | null;
    pedir: () => Promise<Posicion | null>;
    denegado: boolean;
} {
    const [posicion, setPosicion] = useState<Posicion | null>(null);
    const [denegado, setDenegado] = useState(false);

    const pedir = useCallback(async () => {
        if (navigator.geolocation === undefined) {
            return null;
        }

        return new Promise<Posicion | null>((resolver) => {
            navigator.geolocation.getCurrentPosition(
                (p) => {
                    const pos: Posicion = {
                        lat: p.coords.latitude,
                        lng: p.coords.longitude,
                        acc: p.coords.accuracy,
                        at: new Date(p.timestamp).toISOString(),
                    };
                    setPosicion(pos);
                    resolver(pos);
                },
                (e) => {
                    // 1 = PERMISSION_DENIED
                    setDenegado(e.code === 1);
                    resolver(null);
                },
                {
                    // 12 s: dentro de un piso con paredes gruesas el GPS tarda.
                    // Más que eso y la empleada está esperando por nada.
                    timeout: 12_000,
                    maximumAge: 60_000,
                    enableHighAccuracy: true,
                }
            );
        });
    }, []);

    return { posicion, pedir, denegado };
}
